<template>
  <div class="cost-pay">
    <div class="cost-pay__head">
      <div>
        <div class="cost-pay__title">{{ $t('Comparison of cost and payment') }}</div>
        <div class="cost-pay__legend">
          <span class="cost-pay__dot cost-pay__dot--cost" />
          {{ $t('cost') }}
          <span class="cost-pay__dot cost-pay__dot--pay" />
          {{ $t('payment') }}
        </div>
      </div>
    </div>
    <div
      v-if="canSee"
      class="cost-pay__body"
    >
      <apexchart
        v-if="hasData"
        :key="chartKey"
        type="line"
        height="240"
        :options="chartOptions"
        :series="series"
      />
      <div
        v-else
        class="cost-pay__empty"
      >
        <v-progress-circular
          v-if="loading"
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
        <home-empty-state v-else />
      </div>
    </div>
    <div
      v-else
      class="cost-pay__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

export default {
  name: 'HomeCostPayment',
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: '7days'
    }
  },
  data () {
    return {
      items: [],
      loading: false
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    },
    hasData () {
      return this.items.some(item => {
        const cost = parseFloat(item.cost) || 0
        const pay = parseFloat(item.pay) || 0
        return cost > 0 || pay > 0
      })
    },
    chartKey () {
      return this.dateType + '-' + this.items.length
    },
    series () {
      return [
        {
          name: this.$t('cost'),
          data: this.items.map(item => parseFloat(item.cost) || 0)
        },
        {
          name: this.$t('payment'),
          data: this.items.map(item => parseFloat(item.pay) || 0)
        }
      ]
    },
    chartOptions () {
      const self = this
      return {
        chart: {
          type: 'line',
          fontFamily: 'IRANSans',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false }
        },
        colors: ['#4CAF50', '#FF9800'],
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        markers: {
          size: this.items.length > 12 ? 0 : 4,
          colors: ['#fff'],
          strokeColors: ['#4CAF50', '#FF9800'],
          strokeWidth: 2
        },
        grid: {
          borderColor: '#E5E7EB',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } }
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter (value) {
              return self.$numberFormat(value) + ' ' + (self.$store.state.currency.name || '')
            }
          }
        },
        xaxis: {
          categories: this.items.map(item => item.key),
          tickAmount: this.items.length > 12 ? 7 : undefined,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { fontSize: '10px', colors: '#6B7280' }
          }
        },
        yaxis: {
          opposite: !!this.$vuetify.rtl,
          min: 0,
          labels: {
            style: { fontSize: '10px', colors: '#6B7280' },
            offsetX: this.$vuetify.rtl ? -10 : 0,
            formatter (value) {
              return self.$numberFormat(value)
            }
          }
        }
      }
    }
  },
  watch: {
    dateType () {
      this.fetchList()
    }
  },
  mounted () {
    this.fetchList()
  },
  methods: {
    extractItems (data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      if (data && Array.isArray(data.items)) return data.items
      return []
    },
    fetchList () {
      this.loading = true
      this.$axios.get(this.$endpoint + 'widgets', {
        params: {
          type: 'CompareCostByPay',
          dateType: this.dateType
        }
      }).then(res => {
        this.items = this.extractItems(res.data)
      }).catch(() => {
        this.items = []
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.cost-pay {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px 8px;
  min-height: 292px;
}
.cost-pay__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.cost-pay__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.cost-pay__legend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  color: #6B7280;
  white-space: nowrap;
}
.cost-pay__dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.cost-pay__dot--cost {
  background: #4CAF50;
}
.cost-pay__dot--pay {
  background: #FF9800;
}
.cost-pay__body {
  min-height: 240px;
}
.cost-pay__empty {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}
</style>
